import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { PhotoCamera, Close, Delete } from '@mui/icons-material';
import { createManualLogEntry, updateManualLogEntry } from '../services/logService';
import { LogEvent } from '../types/shared';
import { useTranslation } from 'react-i18next';

interface ManualLogEntryModalProps {
    open: boolean;
    onClose: () => void;
    userId: string;
    initialData?: LogEvent;
}

const ManualLogEntryModal: React.FC<ManualLogEntryModalProps> = ({ open, onClose, userId, initialData }) => {
    const { t } = useTranslation();
    const [description, setDescription] = useState('');
    const [photo, setPhoto] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (open) {
            if (initialData) {
                setDescription(initialData.description || '');
                setPhotoPreview(initialData.logImageUrl || null);
            } else {
                setDescription('');
                setPhoto(null);
                setPhotoPreview(null);
            }
        }
    }, [open, initialData]);

    const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePhoto = () => {
        setPhoto(null);
        setPhotoPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSave = async () => {
        if (!description.trim()) return;

        setLoading(true);
        try {
            if (initialData) {
                await updateManualLogEntry(initialData.id, userId, description, photo || undefined);
            } else {
                await createManualLogEntry(userId, description, photo || undefined);
            }
            onClose();
        } catch (error) {
            console.error('Error saving manual log entry:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {initialData ? t('logbook.edit_entry') : t('logbook.new_entry')}
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    disabled={loading}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        autoFocus
                        multiline
                        rows={4}
                        label={t('logbook.description_label')}
                        placeholder={t('logbook.description_placeholder')}
                        fullWidth
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={loading}
                    />

                    <Box>
                        <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="photo-upload"
                            onChange={handlePhotoChange}
                            ref={fileInputRef}
                        />
                        <label htmlFor="photo-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<PhotoCamera />}
                                disabled={loading}
                            >
                                {t('logbook.add_photo')}
                            </Button>
                        </label>
                    </Box>

                    {photoPreview && (
                        <Box sx={{ position: 'relative', mt: 2, borderRadius: 2, overflow: 'hidden' }}>
                            <img
                                src={photoPreview}
                                alt="Preview"
                                style={{ width: '100%', maxHeight: 300, objectFit: 'cover' }}
                            />
                            <IconButton
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                }}
                                onClick={handleRemovePhoto}
                                disabled={loading}
                            >
                                <Delete />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!description.trim() || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    {loading ? t('common.saving') : t('logbook.save_entry')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManualLogEntryModal;
